import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import states from "../data/india_states.json";

export default function UserProfile() {
  const nav = useNavigate();

  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");

  /* ================= LOAD PROFILE ================= */
  useEffect(() => {
    async function loadProfile() {
      try {
        const res = await api.get("/user/me");
        const data = res.data || {};

        const clazzValue =
          data.clazz ||
          data.class_name ||
          data.class ||
          data.class_id ||
          "";

        setUser({
          ...data,
          clazz: clazzValue,
        });

        setSelectedState(data.state || "");
        setSelectedDistrict(data.district || "");
      } catch (err) {
        console.error(err);
        alert("❌ Failed to load profile");
      }
    }

    loadProfile();
  }, []);

  function updateField(key, value) {
    setUser((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  /* ================= SAVE PROFILE ================= */
  async function saveProfile() {
    if (!user) return;

    setSaving(true);
    try {
      await api.put("/user/me", {
        name: user.name || "",
        phone: user.phone || "",
        board_or_prep: user.board_or_prep || "",
        address: user.address || "",
        upi_id: user.upi_id || "",
        bank_name: user.bank_name || "",
        account_number: user.account_number || "",
        ifsc_code: user.ifsc_code || "",
        state: selectedState || "",
        district: selectedDistrict || "",
      });

      alert("✅ Profile updated successfully");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to update profile");
    } finally {
      setSaving(false);
    }
  }

  if (!user) return <p style={{ padding: 20 }}>⏳ Loading profile...</p>;

  return (
    <div style={pageStyle}>
      <div style={overlayStyle}>
        <div style={containerStyle}>
          {/* HEADER */}
          <div style={headerRow}>
            <img src="/logo.png" alt="EduAt10" style={logoStyle} />
            <button
              onClick={() => nav("/user/dashboard")}
              style={backBtnStyle}
            >
              ← Back to Dashboard
            </button>
          </div>

          <h2 style={{ marginBottom: 20 }}>👤 My Profile</h2>

          {/* NAME */}
          <input
            placeholder="Full Name"
            value={user.name || ""}
            onChange={(e) => updateField("name", e.target.value)}
            style={inputStyle}
          />

          {/* PHONE */}
          <input
            placeholder="Mobile Number"
            value={user.phone || ""}
            onChange={(e) => updateField("phone", e.target.value)}
            style={inputStyle}
          />

          {/* CLASS (READ ONLY - AUTO FROM SUBSCRIPTION) */}
          <input
            placeholder="Class"
            value={user.clazz || ""}
            disabled
            style={{
              ...inputStyle,
              background: "#f1f5f9",
              cursor: "not-allowed",
            }}
          />

          {/* BOARD */}
          <input
            placeholder="Board / Preparation"
            value={user.board_or_prep || ""}
            onChange={(e) =>
              updateField("board_or_prep", e.target.value)
            }
            style={inputStyle}
          />

          {/* ADDRESS */}
          <input
            placeholder="Address"
            value={user.address || ""}
            onChange={(e) =>
              updateField("address", e.target.value)
            }
            style={inputStyle}
          />

          {/* UPI */}
          <input
            placeholder="UPI ID"
            value={user.upi_id || ""}
            onChange={(e) =>
              updateField("upi_id", e.target.value)
            }
            style={inputStyle}
          />

          {/* BANK NAME */}
          <input
            placeholder="Bank Name"
            value={user.bank_name || ""}
            onChange={(e) =>
              updateField("bank_name", e.target.value)
            }
            style={inputStyle}
          />

          {/* ACCOUNT NUMBER */}
          <input
            placeholder="Account Number"
            value={user.account_number || ""}
            onChange={(e) =>
              updateField("account_number", e.target.value)
            }
            style={inputStyle}
          />

          {/* IFSC */}
          <input
            placeholder="IFSC Code"
            value={user.ifsc_code || ""}
            onChange={(e) =>
              updateField("ifsc_code", e.target.value)
            }
            style={inputStyle}
          />

          {/* EMAIL (READ ONLY) */}
          <input
            value={user.email || ""}
            disabled
            style={{
              ...inputStyle,
              background: "#f1f5f9",
              cursor: "not-allowed",
            }}
          />

          {/* STATE */}
          <select
            value={selectedState}
            onChange={(e) => {
              setSelectedState(e.target.value);
              setSelectedDistrict("");
            }}
            style={inputStyle}
          >
            <option value="">Select State</option>
            {Object.keys(states).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          {/* DISTRICT */}
          <select
            value={selectedDistrict}
            onChange={(e) => setSelectedDistrict(e.target.value)}
            disabled={!selectedState}
            style={inputStyle}
          >
            <option value="">Select District</option>
            {(states[selectedState] || []).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          {/* SAVE BUTTON */}
          <button
            onClick={saveProfile}
            disabled={saving}
            style={saveBtnStyle}
          >
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const pageStyle = {
  minHeight: "100vh",
  backgroundImage: "url('/eduat10-bg.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
};

const overlayStyle = {
  minHeight: "100vh",
  backgroundColor: "rgba(0,0,0,0.25)",
  padding: 20,
};

const containerStyle = {
  maxWidth: 620,
  margin: "auto",
  background: "rgba(255,255,255,0.95)",
  padding: 30,
  borderRadius: 16,
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
};

const logoStyle = {
  width: 120,
};

const backBtnStyle = {
  padding: "8px 14px",
  background: "#e5e7eb",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

const inputStyle = {
  width: "100%",
  marginBottom: 12,
  padding: 10,
  borderRadius: 8,
  border: "1px solid #cbd5f5",
  fontSize: 14,
};

const saveBtnStyle = {
  marginTop: 10,
  padding: "12px",
  width: "100%",
  background: "#2563eb",
  color: "white",
  border: "none",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
};
